import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export type TableDensity = 'compact' | 'normal' | 'comfortable';

const DENSITIES: TableDensity[] = ['compact', 'normal', 'comfortable'];

const DENSITY_LABELS: Record<TableDensity, string> = {
  compact: 'Compact',
  normal: 'Normal',
  comfortable: 'Comfortable',
};

const DENSITY_DESCRIPTIONS: Record<TableDensity, string> = {
  compact: 'Tighter rows — more data visible at once.',
  normal: 'Balanced spacing for everyday list work.',
  comfortable: 'Roomier rows — easier to scan and tap.',
};

const ROW_COUNT: Record<TableDensity, number> = {
  compact: 6,
  normal: 4,
  comfortable: 3,
};

const ROW_PAD: Record<TableDensity, string> = {
  compact: 'py-0.5 gap-1',
  normal: 'py-1 gap-1.5',
  comfortable: 'py-1.5 gap-2',
};

function DensityPreview({ density }: { density: TableDensity }) {
  const rows = ROW_COUNT[density];
  const pad = ROW_PAD[density];

  return (
    <div className="relative flex h-28 w-full flex-col overflow-hidden rounded-xl border border-black/10 bg-slate-50 shadow-inner dark:border-white/10 dark:bg-slate-900/40">
      <div className="flex h-6 items-center gap-2 border-b border-black/5 bg-white px-2.5 dark:border-white/5 dark:bg-slate-800">
        <span className="h-1.5 w-10 rounded-full bg-slate-300 dark:bg-slate-600" />
        <span className="h-1.5 w-8 rounded-full bg-slate-200 dark:bg-slate-700" />
        <span className="ml-auto h-1.5 w-6 rounded-full bg-[#6b8fb8]/70" />
      </div>
      <div className="flex flex-1 flex-col px-2 py-1.5">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center border-b border-black/[0.04] last:border-0 dark:border-white/[0.04]',
              pad,
            )}
          >
            <span
              className={cn(
                'h-1.5 rounded-full bg-slate-300 dark:bg-slate-600',
                i === 0 ? 'w-[42%]' : i % 2 === 0 ? 'w-[55%]' : 'w-[38%]',
              )}
            />
            <span
              className={cn(
                'ml-auto rounded-full bg-[#6b8fb8]/25',
                density === 'compact' ? 'h-1 w-6' : density === 'normal' ? 'h-1.5 w-7' : 'h-2 w-8',
              )}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export type TableDensityPickerProps = {
  value: TableDensity;
  onChange: (density: TableDensity) => void;
  className?: string;
};

/**
 * Visual table density picker for Account settings → Workspace.
 */
export function TableDensityPicker({ value, onChange, className }: TableDensityPickerProps) {
  const { colors } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Row density"
      className={cn('grid grid-cols-1 gap-3 sm:grid-cols-3', className)}
    >
      {DENSITIES.map((density) => {
        const selected = value === density;
        return (
          <button
            key={density}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(density)}
            className={cn(
              'group relative rounded-2xl border-2 p-3 text-left transition-all',
              selected
                ? cn(colors.primaryBorderAll, 'bg-slate-50/90 shadow-sm dark:bg-white/[0.04]')
                : cn(colors.border, 'border', colors.interactiveHover),
            )}
          >
            <DensityPreview density={density} />
            <div className="mt-3 flex items-start gap-2.5">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  selected
                    ? cn(colors.primary, colors.primaryText, 'border-transparent')
                    : cn(colors.border, 'bg-transparent'),
                )}
                aria-hidden
              >
                {selected && (
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm font-semibold', colors.text)}>
                  {DENSITY_LABELS[density]}
                </span>
                <span className={cn('mt-0.5 block text-xs leading-snug', colors.textMuted)}>
                  {DENSITY_DESCRIPTIONS[density]}
                </span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export type PageSizePickerProps = {
  value: number;
  options: number[];
  onChange: (size: number) => void;
  className?: string;
};

/**
 * Chip-style page size picker — numeric options suit a compact control.
 */
export function PageSizePicker({ value, options, onChange, className }: PageSizePickerProps) {
  const { colors } = useTheme();
  const sizes = options.length > 0 ? options : [50, 100, 200, 500];
  const known = sizes.includes(value);

  return (
    <div
      role="radiogroup"
      aria-label="Default page size"
      className={cn('flex flex-wrap gap-2', className)}
    >
      {sizes.map((size) => {
        const selected = value === size;
        return (
          <button
            key={size}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(size)}
            className={cn(
              'min-w-[4.5rem] rounded-xl border-2 px-4 py-2.5 text-center text-sm font-semibold tabular-nums transition-all',
              selected
                ? cn(colors.primaryBorderAll, colors.primary, colors.primaryText, 'shadow-sm')
                : cn(colors.border, 'border', colors.text, colors.interactiveHover),
            )}
          >
            {size}
          </button>
        );
      })}
      {!known && (
        <button
          type="button"
          role="radio"
          aria-checked
          className={cn(
            'min-w-[4.5rem] rounded-xl border-2 px-4 py-2.5 text-center text-sm font-semibold tabular-nums shadow-sm',
            colors.primaryBorderAll,
            colors.primary,
            colors.primaryText,
          )}
        >
          {value}
        </button>
      )}
    </div>
  );
}

export default TableDensityPicker;
